import { delegatePackageConstants } from "@constants";
import { constructionService, delegatePackageService } from "@services";
import { constructionActions, maskActions } from "@actions";
import { msgTypes, showModal } from "@utils";
import { find } from "lodash";

export const delegatePackageActions = {
  /**
   * 取得寄物包裹列表
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} condition - 查詢條件
   * @param {function} cbSuccess - 成功後的 callback
   * @param {function} cbError - 失敗後的 callback
   * @param {object} options - 選項
   * @returns
   */
  getAll: (constructionUuid, condition, cbSuccess, cbError, options) => {
    return dispatch => {
      if (options?.loading) {
        dispatch(maskActions.toggleMask(true));
      }

      dispatch({ type: delegatePackageConstants.GET_ALL_REQUEST });

      delegatePackageService
        .getAll(constructionUuid, condition)
        .then(
          ({
            items,
            total,
            total_wait_collect_packages,
            total_delegate_recipient_packages,
            total_delegate_sender_packages
          }) => {
            dispatch({
              type: delegatePackageConstants.GET_ALL_SUCCESS,
              constructionUuid,
              items,
              total,
              total_wait_collect_packages,
              total_delegate_recipient_packages,
              total_delegate_sender_packages,
              condition
            });

            cbSuccess && cbSuccess(items);
          },
          error => {
            cbError && cbError(error);
            dispatch({
              type: delegatePackageConstants.GET_ALL_FAILURE,
              error
            });
          }
        )
        .finally(() => {
          if (options?.loading) {
            dispatch(maskActions.toggleMask(false));
          }
        });
    };
  },
  /**
   * 重置寄物包裹列表
   * @returns
   */
  resetAll: () => ({
    type: delegatePackageConstants.RESET_ALL_SUCCESS
  }),
  /**
   * 刪除寄物包裹
   * @param {number} id - 包裹 id
   * @param {string} constructionUuid - 建案 uuid
   * @returns
   */
  delete: (id, constructionUuid) => {
    return (dispatch, getState) => {
      dispatch(maskActions.toggleMask(true));
      delegatePackageService
        .delete(id)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            showModal(msgTypes.SUCCESS, "通知", response, () => {
              delegatePackageActions.__getAll(dispatch, getState);
            });
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  __getAll: (dispatch, getState) => {
    const { delegatePackages } = getState();
    dispatch(
      delegatePackageActions.getAll(
        delegatePackages?.query?.constructionUuid,
        delegatePackages?.query?.condition
      )
    );
  },
  /**
   * 領取寄物包裹
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  collect: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      delegatePackageService
        .collect(constructionUuid, info)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 取得包裹類型選項
   * @returns
   */
  getTypeOptions: () => {
    return dispatch => {
      dispatch({
        type: delegatePackageConstants.GET_TYPE_OPTIONS_REQUEST
      });

      constructionService.getOperateOptionList("delegate_package_type").then(
        typeOptions => {
          dispatch({
            type: delegatePackageConstants.GET_TYPE_OPTIONS_SUCCESS,
            typeOptions: typeOptions.map(option => ({
              ...option,
              key: option.id,
              value: option.id
            }))
          });
        },
        error => {
          dispatch({
            type: delegatePackageConstants.GET_TYPE_OPTIONS_FAILURE,
            error
          });
        }
      );
    };
  },
  /**
   * 重設包裹類型選項
   * @returns
   */
  resetTypeOptions: () => ({
    type: delegatePackageConstants.RESET_TYPE_OPTIONS_SUCCESS
  }),
  /**
   * 取得寄物包裹資訊
   * @param {number} id - 貨運包裹 id
   * @param {function} cbSuccess - 成功後的 callback
   * @param {function} cbError - 失敗後的 callback
   * @returns
   */
  get: (id, cbSuccess, cbError) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      delegatePackageService
        .get(id)
        .then(
          delegatePackage => {
            const { history, status } = delegatePackage;

            let currentHistoryStatus = null;
            if (status === "wait_collect") {
              currentHistoryStatus = find(history, { status: "wait_collect" });
            }

            const transferedDelegatePackage = {
              ...delegatePackage,
              sender_household_label: delegatePackage.sender_household_id_number,
              sender_name: delegatePackage.sender_name || delegatePackage.sender_username,
              sender_type: delegatePackage.sender_household_id ? "household" : "other",
              recipient_household_label: delegatePackage.recipient_household_id_number,
              recipient_name: delegatePackage.recipient_name || delegatePackage.recipient_username,
              recipient_type: delegatePackage.recipient_household_id ? "household" : "other",

              /**
               * 2023-05-29
               * 後端將原物品包裹的狀態改成 history 的陣列格式，但前端畫面呈現未調整，故將後端資料轉換成前端原本的格式。
               **/
              ...(currentHistoryStatus
                ? {
                    files: currentHistoryStatus.files,
                    note: currentHistoryStatus.note,
                    status_created_at: currentHistoryStatus.created_at
                  }
                : {})
            };

            cbSuccess && cbSuccess(transferedDelegatePackage);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error, cbError);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 新增寄物包裹資訊
   * @param {string} constructionUuid - 建案 uuid
   * @param {object} info - 包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   *
   * @returns
   */
  create: (constructionUuid, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      delegatePackageService
        .create(constructionUuid, info)
        .then(
          response => {
            dispatch(constructionActions.getSummaries(constructionUuid));
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  },
  /**
   * 更新寄物包裹資訊
   * @param {number} id - 寄物包裹 id
   * @param {object} info - 寄物包裹資訊
   * @param {function} cbSuccess - 成功後的 callback
   * @returns
   */
  update: (id, info, cbSuccess) => {
    return dispatch => {
      dispatch(maskActions.toggleMask(true));
      delegatePackageService
        .update(id, info)
        .then(
          response => {
            cbSuccess && cbSuccess(response);
          },
          error => {
            showModal(msgTypes.ERROR, "錯誤", error);
          }
        )
        .finally(() => {
          dispatch(maskActions.toggleMask(false));
        });
    };
  }
};
